import type { ProjectClientSide } from '@readme/backend/models/project/types';
import type { HubResponseProps, I18nLanguage } from '@readme/iso';

import React, { useContext } from 'react';
import { useLocation } from 'react-router-dom';

import type { ProjectContextValue, UserContextValue } from '@core/context';
import { ProjectContext, UserContext } from '@core/context';
import useEnvInfo from '@core/hooks/useEnvInfo';
import useUserPermissions from '@core/hooks/useUserPermissions';

import { useSuperHubStore } from '.';

export interface InitializeSuperHubStoreProps {
  children: React.ReactNode;
  currentBranch?: HubResponseProps['branch'];
  currentVersion: HubResponseProps['gitVersion'];
  language?: I18nLanguage;
}

const getSlimProjectObject = (project?: { _id; name; subdomain }) => ({
  id: project?._id,
  name: project?.name,
  subdomain: project?.subdomain,
});

/**
 * Middleware component connects to our App project and user contexts to then
 * initialize our SuperHub store and make it ready for this particular project.
 */
export function InitializeSuperHubStore({
  children,
  currentBranch,
  currentVersion,
  language,
}: InitializeSuperHubStoreProps) {
  const { isServer } = useEnvInfo();
  const [initialize, isReady] = useSuperHubStore(s => [s.initialize, s.isReady]);
  const { isAdminUser, isEditorUser, isGroupAdminUser, isOwner } = useUserPermissions();
  const { project } = useContext(ProjectContext) as ProjectContextValue;
  const { isAuthenticated, user, stripeId } = useContext(UserContext) as unknown as UserContextValue;
  const location = useLocation();

  // To enable SSR, we must call this inline rather than inside an effect. On
  // the server, ignore the "ready" state b/c our global stores on the server
  // persist indefinitely and won't ever reset the "ready" state to false.
  if (isServer || !isReady) {
    const isEnterpriseGroup = !!project.childrenProjects?.length;
    const isEnterpriseChild = !!project.parent;
    const enterpriseParent = isEnterpriseGroup ? project : project.parent;
    const enterpriseProjects = (
      project.childrenProjects?.length ? project.childrenProjects : project.siblings
    ) as ProjectClientSide[];

    initialize({
      currentBranch: currentBranch || currentVersion,
      isEnterpriseGroup,
      isEnterpriseChild,
      enterpriseParent: getSlimProjectObject(enterpriseParent),
      enterpriseProjects: enterpriseProjects?.map(getSlimProjectObject),
      language: language || 'en',
      // Because hash route values are not available on the server, initialize
      // both SSR and the first client render without the location.hash to avoid
      // SSR/CSR mismatches.
      location: [location.pathname],
      projectFlags: project.flags,
      subdomain: project.subdomain,
      user: {
        email: user?.email || null,
        id: user?.teammateUserId || null,
        isAuthenticated,
        isGroupAdmin: isGroupAdminUser,
        isOwner,
        name: user?.name || null,
        role: isAdminUser ? 'admin' : isEditorUser ? 'editor' : 'viewer',
        stripeId: stripeId || null,
      },
    });
  }

  return <>{children}</>;
}
